import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getMinecraftResources, toggleLike, MinecraftResourceType } from '../../firebase/minecraft';
import { MinecraftResource } from '../../types/minecraft';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import SearchIcon from '../../components/icons/SearchIcon';
import PlusIcon from '../../components/icons/PlusIcon';

const MinecraftEducationMain: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [allResources, setAllResources] = useState<MinecraftResource[]>([]);
  const [worldResources, setWorldResources] = useState<MinecraftResource[]>([]);
  const [modResources, setModResources] = useState<MinecraftResource[]>([]);
  const [codeResources, setCodeResources] = useState<MinecraftResource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const [allData, worldData, modData, codeData] = await Promise.all([
          getMinecraftResources(undefined),
          getMinecraftResources('world'),
          getMinecraftResources('mod'),
          getMinecraftResources('code')
        ]);
        
        setAllResources(allData);
        setWorldResources(worldData);
        setModResources(modData);
        setCodeResources(codeData);
      } catch (error) {
        console.error('리소스 로드 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  // 인기 리소스 (좋아요 수 기준)
  const popularResources = [...allResources]
    .sort((a, b) => b.likes - a.likes)
    .slice(0, 6);
  
  // 최근 업로드된 리소스
  const recentResources = [...allResources]
    .sort((a, b) => {
      const aTime = a.createdAt?.getTime() || 0;
      const bTime = b.createdAt?.getTime() || 0;
      return bTime - aTime;
    })
    .slice(0, 6);

  const handleLike = async (resource: MinecraftResource) => {
    if (!currentUser) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      await toggleLike(resource.id, currentUser.uid, resource.likes, resource.likedBy);
      
      // 로컬 상태 업데이트
      const updateResource = (r: MinecraftResource) => {
        if (r.id === resource.id) {
          const isLiked = r.likedBy.includes(currentUser.uid);
          return {
            ...r,
            likes: isLiked ? r.likes - 1 : r.likes + 1,
            likedBy: isLiked 
              ? r.likedBy.filter(id => id !== currentUser.uid)
              : [...r.likedBy, currentUser.uid]
          };
        }
        return r;
      };
      
      setAllResources(prev => prev.map(updateResource));
      setWorldResources(prev => prev.map(updateResource));
      setModResources(prev => prev.map(updateResource));
      setCodeResources(prev => prev.map(updateResource));
    } catch (error) {
      console.error('좋아요 처리 오류:', error);
      alert('좋아요 처리 중 오류가 발생했습니다.');
    }
  };

  const handleDownload = async (resource: MinecraftResource) => {
    try {
      window.open(resource.fileUrl, '_blank');
      const { incrementDownloadCount } = await import('../../firebase/minecraft');
      await incrementDownloadCount(resource.id, resource.downloadCount);
      
      // 로컬 상태 업데이트
      const updateResource = (r: MinecraftResource) => {
        if (r.id === resource.id) {
          return { ...r, downloadCount: r.downloadCount + 1 };
        }
        return r;
      };
      
      setAllResources(prev => prev.map(updateResource));
      setWorldResources(prev => prev.map(updateResource));
      setModResources(prev => prev.map(updateResource));
      setCodeResources(prev => prev.map(updateResource));
    } catch (error) {
      console.error('다운로드 오류:', error);
    }
  };

  const getTypeLabel = (type: MinecraftResourceType) => {
    switch (type) {
      case 'world':
        return '월드';
      case 'mod':
        return '모드';
      case 'code':
        return '코드';
      default:
        return '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getTypeIcon = (type: MinecraftResourceType): string => {
    switch (type) {
      case 'world':
        return '🗺️';
      case 'mod':
        return '🧩';
      case 'code':
        return '💻';
      default:
        return '🗺️';
    }
  };

  // 리소스 카드 렌더링 함수
  const renderResourceCard = (resource: MinecraftResource) => (
    <Card
      key={resource.id}
      width="100%"
      padding="0"
      alignItems="stretch"
      gap={0}
      style={{
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      onClick={() => navigate(`/minecraft-education/${resource.id}`)}
    >
      {/* 썸네일 */}
      <div style={{
        width: '100%',
        height: '200px',
        background: '#F0F0F0',
                        display: 'flex',
                        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
                      {resource.thumbnailUrl ? (
          <img
                          src={resource.thumbnailUrl}
                          alt={resource.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
                        />
                      ) : (
          <div style={{
            fontSize: '64px'
          }}>
                          {getTypeIcon(resource.type)}
          </div>
                      )}
      </div>

      {/* 콘텐츠 */}
      <div style={{
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        flex: 1
      }}>
        {/* 제목 및 타입 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <h3 style={{
            margin: 0,
            color: 'var(--Color-text-normal, #161616)',
            fontFamily: 'Pretendard',
            fontSize: '16px',
            fontWeight: '600',
            lineHeight: '24px',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
                            {resource.title}
          </h3>
          <div style={{
            padding: '4px 8px',
            borderRadius: '4px',
            background: 'var(--Color-fill-brand-color, #FF6F0F)',
            color: 'white',
            fontSize: '12px',
            fontFamily: 'Pretendard',
            fontWeight: '500',
            whiteSpace: 'nowrap'
          }}>
            {getTypeLabel(resource.type)}
          </div>
        </div>
                        
        {/* 설명 */}
        <p style={{
          margin: 0,
          color: 'var(--Color-text-assistive-strong, #4C4C4C)',
          fontFamily: 'Pretendard',
          fontSize: '13px',
          fontWeight: '400',
          lineHeight: '19.5px',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
                        }}>
                          {resource.description}
        </p>
                        
        {/* 메타 정보 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 'auto'
        }}>
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontFamily: 'Pretendard',
            fontSize: '12px',
            fontWeight: '400',
            lineHeight: '18px'
          }}>
            {resource.authorName}
          </div>
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontFamily: 'Pretendard',
            fontSize: '12px',
            fontWeight: '400',
            lineHeight: '18px'
          }}>
            좋아요 {resource.likes} • 다운로드 {resource.downloadCount}
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div
      style={{ 
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      {/* 헤더 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        alignSelf: 'stretch',
        padding: '8px 0px 24px 0px'
      }}>
        <h1 style={{ 
          color: 'var(--Color-text-normal, #161616)',
          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
          fontSize: 'var(--Font-Font-size-Heading-2, 32px)',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '150%',
          letterSpacing: '-0.64px',
          margin: 0
        }}>
          ⛏️ 마인크래프트 에듀케이션
        </h1>
      </div>

      {/* 소개 섹션 */}
      <Card
        width="100%"
        padding="32px 24px"
        alignItems="flex-start"
        gap={16}
        style={{
          marginBottom: '32px',
          background: 'linear-gradient(135deg, #FF6F0F 0%, #FF9F5E 100%)'
        }}
      >
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <h2 style={{
            margin: 0,
            color: 'white',
            fontFamily: 'Pretendard',
            fontSize: '24px',
            fontWeight: '600',
            lineHeight: '36px'
          }}>
            마인크래프트 에듀케이션에 오신 것을 환영합니다
          </h2>
          <p style={{
            margin: 0,
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: 'Pretendard',
            fontSize: '16px',
            fontWeight: '400',
            lineHeight: '24px'
          }}>
            교육용 마인크래프트 월드, 모드, 코드를 공유하고 다운로드할 수 있는 공간입니다. 
            학생들과 함께 창의적인 학습 경험을 만들어보세요.
          </p>
        </div>
      </Card>

      {/* 카테고리별 빠른 링크 */}
      <div style={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '24px',
        marginBottom: '48px'
      }}>
        <div
          style={{
            cursor: 'pointer',
            transition: 'transform 0.2s, boxShadow 0.2s',
            width: '100%',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
            background: 'var(--Color-background-normal, #FFF)',
            borderRadius: '16px',
            boxShadow: '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 5px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05))'
          }}
          onClick={() => navigate('/minecraft-education/world-share')}
          onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 5px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05))';
          }}
        >
          <div style={{
            fontSize: '48px',
            marginBottom: '8px'
          }}>
            🗺️
          </div>
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <h3 style={{
              margin: 0,
              color: 'var(--Color-text-normal, #161616)',
              fontFamily: 'Pretendard',
              fontSize: '18px',
              fontWeight: '600',
              lineHeight: '27px'
            }}>
              월드 공유
            </h3>
            <p style={{
              margin: 0,
              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
              fontFamily: 'Pretendard',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '21px'
            }}>
              교육용 마인크래프트 월드를 탐색하고 공유하세요
            </p>
            <div style={{
              marginTop: '8px',
              color: 'var(--Color-text-assistive, #989898)',
              fontFamily: 'Pretendard',
              fontSize: '13px',
              fontWeight: '400'
            }}>
              총 {worldResources.length}개의 월드
            </div>
          </div>
        </div>

        <div
          style={{
            cursor: 'pointer',
            transition: 'transform 0.2s, boxShadow 0.2s',
            width: '100%',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
            background: 'var(--Color-background-normal, #FFF)',
            borderRadius: '16px',
            boxShadow: '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 5px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05))'
          }}
          onClick={() => navigate('/minecraft-education/mod-guide')}
          onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 5px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05))';
          }}
        >
          <div style={{
            fontSize: '48px',
            marginBottom: '8px'
          }}>
            🧩
          </div>
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <h3 style={{
              margin: 0,
              color: 'var(--Color-text-normal, #161616)',
              fontFamily: 'Pretendard',
              fontSize: '18px',
              fontWeight: '600',
              lineHeight: '27px'
            }}>
              모드 안내
            </h3>
            <p style={{
              margin: 0,
              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
              fontFamily: 'Pretendard',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '21px'
            }}>
              다양한 교육용 모드를 찾아보고 활용하세요
            </p>
            <div style={{
              marginTop: '8px',
              color: 'var(--Color-text-assistive, #989898)',
              fontFamily: 'Pretendard',
              fontSize: '13px',
              fontWeight: '400'
            }}>
              총 {modResources.length}개의 모드
            </div>
          </div>
        </div>

        <div
          style={{
            cursor: 'pointer',
            transition: 'transform 0.2s, boxShadow 0.2s',
            width: '100%',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
            background: 'var(--Color-background-normal, #FFF)',
            borderRadius: '16px',
            boxShadow: '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 5px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05))'
          }}
          onClick={() => navigate('/minecraft-education/makecode')}
          onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 5px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05))';
                        }}
        >
          <div style={{
            fontSize: '48px',
            marginBottom: '8px'
          }}>
            💻
          </div>
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <h3 style={{
              margin: 0,
              color: 'var(--Color-text-normal, #161616)',
              fontFamily: 'Pretendard',
              fontSize: '18px',
              fontWeight: '600',
              lineHeight: '27px'
            }}>
              Makecode 공유
            </h3>
            <p style={{
              margin: 0,
              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
              fontFamily: 'Pretendard',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '21px'
            }}>
              Makecode로 작성한 코드를 공유하고 학습하세요
            </p>
            <div style={{
              marginTop: '8px',
              color: 'var(--Color-text-assistive, #989898)',
              fontFamily: 'Pretendard',
              fontSize: '13px',
              fontWeight: '400'
            }}>
              총 {codeResources.length}개의 코드
            </div>
          </div>
        </div>
      </div>

      {/* 인기 리소스 */}
      {popularResources.length > 0 && (
        <div style={{
          width: '100%',
          marginBottom: '48px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 style={{
              margin: 0,
              color: 'var(--Color-text-normal, #161616)',
              fontFamily: 'Pretendard',
              fontSize: '20px',
              fontWeight: '600',
              lineHeight: '30px'
            }}>
              🔥 인기 리소스
            </h2>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px'
          }}>
            {popularResources.map(resource => renderResourceCard(resource))}
          </div>
        </div>
                        )}

      {/* 최근 업로드된 리소스 */}
      {recentResources.length > 0 && (
        <div style={{
          width: '100%'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 style={{
              margin: 0,
              color: 'var(--Color-text-normal, #161616)',
              fontFamily: 'Pretendard',
              fontSize: '20px',
              fontWeight: '600',
              lineHeight: '30px'
            }}>
              ✨ 최근 업로드된 리소스
            </h2>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px'
          }}>
            {recentResources.map(resource => renderResourceCard(resource))}
          </div>
        </div>
      )}

      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '80px 0',
          color: 'var(--Color-text-assistive, #989898)',
          fontFamily: 'Pretendard',
          fontSize: '14px'
        }}>
          로딩 중...
        </div>
      )}

      {!loading && allResources.length === 0 && (
        <Card
          width="100%"
          padding="64px 24px"
          alignItems="center"
          gap={10}
        >
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontFamily: 'Pretendard',
            fontSize: '16px',
            fontWeight: '400',
            lineHeight: '24px',
            textAlign: 'center'
          }}>
            업로드된 리소스가 없습니다.
          </div>
                </Card>
        )}
    </div>
  );
};

export default MinecraftEducationMain;

